class SearchBar extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.sectionId = this.dataset.sectionId;
    this.searchType = this.dataset.searchType;
    this.resultIds = (this.dataset.resultIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    this.hasFacets = this.dataset.hasFacets !== 'false';
    this.controller = null;
    this.debouncedOnInput = this.debounce((event) => {
      this.onChange(event);
    }, 300);

    this.input.addEventListener('input', this.debouncedOnInput.bind(this));
    this.onDocumentClick = this.onDocumentClick.bind(this);
  }

  connectedCallback() {
      document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      if (this.debouncedOnInput?.cancel) this.debouncedOnInput.cancel();
      if (this.controller) this.controller.abort();
  }

  onDocumentClick(e) {
      const clearBtn = e.target.closest('.js-clear-search');
      if (clearBtn && this.contains(clearBtn)) {
          this.onClearSearch(e);
      }
  }

  onClearSearch(e) {
      e.preventDefault();
      this.input.value = '';
      this.onChange();
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (this.controller) {
      this.controller.abort();
    }
    this.controller = new AbortController();
    const signal = this.controller.signal;

    if (!searchTerm.length) {
      this.fetchResults(window.location.pathname, signal);
    } else {
      const searchBaseUrl = window.routes && window.routes.search_url ? window.routes.search_url : this.input.form.action;
      const query = /[\p{L}\p{N}]$/u.test(searchTerm) ? `${searchTerm}*` : searchTerm;
      const searchURL = `${searchBaseUrl}?q=${encodeURIComponent(query)}&type=${this.searchType}`;
      this.fetchResults(searchURL, signal);
    }
  }

  fetchResults(url, signal) {
    const sectionUrl = `${url}${url.includes('?') ? '&' : '?'}section_id=${this.sectionId}`;
    const cleanUrl = `${window.location.pathname}?section_id=${this.sectionId}`;

    const primaryResult = document.getElementById(this.resultIds[0]);
    const gridLoadingTarget = this.hasFacets
      ? primaryResult && primaryResult.querySelector('.collection')
      : primaryResult;
    if (gridLoadingTarget) gridLoadingTarget.classList.add('loading');

    let loadingSpinners = [];
    if (this.hasFacets) {
      loadingSpinners = document.querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
      );
      loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    }

    const isSearching = this.input.value.trim().length > 0;
    const requests = [fetch(sectionUrl, { signal }).then((r) => r.text())];

    // Facets: when searching we need the clean (unfiltered) facets; when clearing,
    // the main request already IS the clean state.
    let fetchingFacets = false;
    if (this.hasFacets && isSearching) {
      if (this.cachedFacets) {
        requests.push(Promise.resolve(this.cachedFacets));
      } else {
        fetchingFacets = true;
        requests.push(fetch(cleanUrl, { signal }).then((r) => r.text()));
      }
    }

    Promise.all(requests)
      .then(([searchResponse, facetsResponse]) => {
        const html = new DOMParser().parseFromString(searchResponse, 'text/html');

        if (!this.hasFacets) {
          this.renderResults(html, null);
        } else if (isSearching) {
          if (fetchingFacets) this.cachedFacets = facetsResponse;
          this.renderResults(html, facetsResponse);
        } else {
          this.renderResults(html, searchResponse);
        }
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        console.error('Error fetching search results:', e);
      })
      .finally(() => {
        if (gridLoadingTarget) gridLoadingTarget.classList.remove('loading');
        loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
      });
  }

  renderResults(html, facetsResponse) {
    this.resultIds.forEach((id) => {
      const sourceElement = html.getElementById(id);
      const destinationElement = document.getElementById(id);
      if (sourceElement && destinationElement) {
        destinationElement.innerHTML = sourceElement.innerHTML;
      }
    });

    if (!this.hasFacets) return;

    if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.renderFilters && facetsResponse) {
        FacetFiltersForm.renderFilters(facetsResponse);
    }
    this.cleanFiltersVisuals();
    this.updateURL();
  }

  cleanFiltersVisuals() {
    const filterForms = document.querySelectorAll('facet-filters-form form');
    filterForms.forEach(form => {
        form.querySelectorAll('input[type="checkbox"]').forEach(input => input.checked = false);
    });
  }

  updateURL() {
    const url = new URL(window.location.href);
    const keys = Array.from(url.searchParams.keys());
    let changed = false;
    keys.forEach(key => {
      if (key.startsWith('filter.')) {
        url.searchParams.delete(key);
        changed = true;
      }
    });

    if (changed) {
        history.replaceState({ path: url.toString() }, '', url.toString());
    }
  }

  debounce(fn, wait) {
    let t;
    const debounced = (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
    debounced.cancel = () => clearTimeout(t);
    return debounced;
  }
}

if (!customElements.get('search-bar')) {
  customElements.define('search-bar', SearchBar);
}
